import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from './use-auth'
import { authorizedFetch } from '@/lib/api'

export interface Entity {
  id: string
  name: string
  entity_type: string
  relationship_type: string
}

export interface MemoryRelationRef {
  memory_id: string
  relation_type: string
  strength: number
  direction: 'outgoing' | 'incoming'
}

export interface Memory {
  id: string
  user_id: string
  content: string
  memory_type: string
  interaction_type: string
  category: string
  subcategory: string
  importance: string
  created_at: string
  thread_id?: string
  message_id?: string
  source_message_text?: string
  entities?: Entity[]
  related_memories?: string[]
  relations?: MemoryRelationRef[]
  is_archived?: boolean
}

export interface MemoryUpdate {
  content?: string
  category?: string
  subcategory?: string
  importance?: string
  memory_type?: string
}

export function useMemories(params?: {
  category?: string
  importance?: string
  entity_id?: string
  thread_id?: string
  is_archived?: boolean
  sort_by?: string
}) {
  const { token, user } = useAuth()
  const queryClient = useQueryClient()

  const queryParams = new URLSearchParams()
  if (user?.id) queryParams.append('user_id', String(user.id))
  if (params?.category) queryParams.append('category', params.category)
  if (params?.importance) queryParams.append('importance', params.importance)
  if (params?.entity_id) queryParams.append('entity_id', params.entity_id)
  if (params?.thread_id) queryParams.append('thread_id', params.thread_id)
  if (params?.is_archived !== undefined) queryParams.append('is_archived', String(params.is_archived))
  if (params?.sort_by) queryParams.append('sort_by', params.sort_by)

  const memoriesQuery = useQuery({
    queryKey: ['memories', user?.id, params],
    queryFn: async (): Promise<Memory[]> => {
      if (!token || !user) throw new Error('Not authenticated')

      const response = await authorizedFetch(`/api/memories/?${queryParams.toString()}`)

      if (!response.ok) {
        throw new Error('Failed to fetch memories')
      }

      return response.json()
    },
    enabled: !!token && !!user,
  })

  const updateMutation = useMutation({
    mutationFn: async ({ memoryId, updates }: { memoryId: string; updates: MemoryUpdate }) => {
      if (!token) throw new Error('Not authenticated')
      
      const response = await authorizedFetch(`/api/memories/${memoryId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to update memory')
      }

      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memories'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (memoryId: string) => {
      if (!token) throw new Error('Not authenticated')
      
      const response = await authorizedFetch(`/api/memories/${memoryId}`, {
        method: 'DELETE',
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to delete memory')
      }

      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memories'] })
    },
  })

  return {
    memories: memoriesQuery.data || [],
    isLoading: memoriesQuery.isLoading,
    error: memoriesQuery.error,
    refetch: memoriesQuery.refetch,
    updateMemory: updateMutation.mutateAsync,
    deleteMemory: deleteMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  }
}

export function useMemoryEntities(memoryId: string | null) {
  const { token } = useAuth()

  return useQuery({
    queryKey: ['memory-entities', memoryId],
    queryFn: async (): Promise<Entity[]> => {
      if (!token || !memoryId) throw new Error('Not authenticated or no memory ID')

      const response = await authorizedFetch(`/api/memories/${memoryId}/entities`)

      if (!response.ok) {
        throw new Error('Failed to fetch memory entities')
      }

      return response.json()
    },
    enabled: !!token && !!memoryId,
  })
}

export function useRelatedMemories(memoryId: string | null) {
  const { token } = useAuth()

  return useQuery({
    queryKey: ['related-memories', memoryId],
    queryFn: async (): Promise<Memory[]> => {
      if (!token || !memoryId) throw new Error('Not authenticated or no memory ID')

      const response = await authorizedFetch(`/api/memories/${memoryId}/related`)

      if (!response.ok) {
        throw new Error('Failed to fetch related memories')
      }

      return response.json()
    },
    enabled: !!token && !!memoryId,
  })
}















